/**
 * Profile — Identity card
 *
 * Presentational: photo + name/email/job-title fields. Controlled by
 * ProfileInfoSection, which owns the form state and image handlers.
 */

import { Login } from '../../../apis/api-client';
import { SettingsCard } from '../../common';
import { Avatar, Box, Button, CircularProgress, Grid, TextField, Typography } from '../../ui';

interface ProfileIdentityCardProps {
  user: Login;
  formData: {
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
  };
  validationErrors: any;
  canEdit: boolean;
  isOwnProfile: boolean;
  imagePreview: string | null;
  resolvedImageUrl: string | null;
  photoSaving: boolean;
  onImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onImageRemove: () => void;
  onChange: (field: string, value: any) => void;
}

export default function ProfileIdentityCard({
  user,
  formData,
  validationErrors,
  canEdit,
  isOwnProfile,
  imagePreview,
  resolvedImageUrl,
  photoSaving,
  onImageSelect,
  onImageRemove,
  onChange,
}: ProfileIdentityCardProps) {
  return (
    <SettingsCard title="Identity">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Photo row. Your own photo is always editable and saves the
            moment you pick (or remove) it, through the self-service
            /Auth/me endpoint — independent of the workspace self-edit flag
            that locks the rest of the form. Admins editing another user's
            photo save it with the rest of the profile via the main Save bar. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <Avatar
            src={imagePreview || resolvedImageUrl || '/user.png'}
            alt={user.firstName || 'User'}
            sx={{ width: 72, height: 72, fontSize: 28, flexShrink: 0 }}
          />
          {isOwnProfile || canEdit ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button variant="outlined" component="label" size="small" disabled={photoSaving}>
                  {imagePreview || resolvedImageUrl ? 'Replace' : 'Upload'} photo
                  <input hidden accept="image/*" type="file" onChange={onImageSelect} />
                </Button>
                {(imagePreview || resolvedImageUrl) && (
                  <Button variant="text" size="small" color="error" onClick={onImageRemove} disabled={photoSaving}>
                    Remove
                  </Button>
                )}
                {photoSaving && <CircularProgress size={16} />}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {isOwnProfile
                  ? 'JPG or PNG · saved automatically.'
                  : 'JPG or PNG · saved with the rest of the profile.'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Profile photo
            </Typography>
          )}
        </Box>

        {/* Identity fields */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="First name"
              fullWidth
              value={formData.firstName}
              onChange={(e) => onChange('firstName', e.target.value)}
              error={!!validationErrors.firstName}
              helperText={validationErrors.firstName}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Last name"
              fullWidth
              value={formData.lastName}
              onChange={(e) => onChange('lastName', e.target.value)}
              error={!!validationErrors.lastName}
              helperText={validationErrors.lastName}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={(e) => onChange('email', e.target.value)}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Job title"
              fullWidth
              value={formData.jobTitle}
              onChange={(e) => onChange('jobTitle', e.target.value)}
              placeholder="e.g. Practice Manager"
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
      </Box>
    </SettingsCard>
  );
}
